import React from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from 'recharts';

const PerformanceDistribution = ({ filteredData, performanceConfig }) => {

    const getPerformanceDistribution = (assessmentResults) => {
        const distribution = {};
        assessmentResults.forEach(user => {
            const level = user.json.finalInterpretation.name;
            distribution[level] = (distribution[level] || 0) + 1;
        });

        // Colors ordered by percentage (highest to lowest)
        const percentageBasedColors = [
            '#4caf50', // Green for highest percentage
            '#2196f3', // Blue for second highest
            '#ff9800', // Orange for third highest
            '#f44336', // Red for fourth highest
            '#9c27b0'  // Purple for lowest percentage
        ];

        // Sort entries by count (highest to lowest percentage)
        const sortedEntries = Object.entries(distribution).sort(([, countA], [, countB]) => countB - countA);

        return sortedEntries.map(([name, count], index) => {
            // Assign colors based on percentage ranking (highest gets first color)
            const color = percentageBasedColors[index] || percentageBasedColors[percentageBasedColors.length - 1];
            
            return {
                name: name.replace(/[🌟💎⚙️🛠🔴❓?�]/g, '').replace(/\?+/g, '').replace(/�+/g, '').trim(),
                fullName: name,
                count,
                color: color
            };
        });
    };

    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Box sx={{ px: 3, mb: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '@media print': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        border: '1px solid #ddd',
                        mb: 2
                    }
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        mb: 3,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: '#0a0068',
                        fontFamily: 'Poppins'
                    }}
                >
                    Performance Distribution by Assessment
                </Typography>
                <Grid container spacing={1.5}>
                    {filteredData.map((assessment, index) => {
                        const distribution = getPerformanceDistribution(assessment.assessmentResults);
                        return (
                            <Grid 
                                item 
                                xs={12} 
                                sm={6} 
                                md={6} 
                                lg={4} 
                                xl={4}
                                key={assessment.id}
                                sx={{
                                    '@media print': {
                                        xs: 6,
                                        sm: 6,
                                        md: 6,
                                        pageBreakInside: 'avoid',
                                        breakInside: 'avoid'
                                    }
                                }}
                            >
                                <Paper 
                                    elevation={1} 
                                    sx={{ 
                                        p: 1.5, 
                                        textAlign: 'center', 
                                        borderRadius: 2,
                                        height: '260px',
                                        '@media print': {
                                            pageBreakInside: 'avoid',
                                            breakInside: 'avoid',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                            border: '1px solid #eee',
                                            height: 'auto',
                                            minHeight: '250px',
                                            p: 1.5
                                        }
                                    }}
                                >
                                    <Typography 
                                        variant="subtitle2" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            mb: 1,
                                            fontSize: '0.85rem',
                                            '@media print': {
                                                fontSize: '0.8rem',
                                                mb: 1
                                            }
                                        }} 
                                        color="#0a0068"
                                    >
                                        {assessment.assignment_name}
                                    </Typography>
                                    <Box sx={{ 
                                        width: '100%', 
                                        height: 180,
                                        '@media print': {
                                            height: '180px',
                                            '& .recharts-wrapper': {
                                                '-webkit-print-color-adjust': 'exact',
                                                'color-adjust': 'exact',
                                                'print-color-adjust': 'exact'
                                            },
                                            '& svg': {
                                                '-webkit-print-color-adjust': 'exact',
                                                'color-adjust': 'exact',
                                                'print-color-adjust': 'exact'
                                            }
                                        }
                                    }}>
                                        <ResponsiveContainer 
                                            width="100%" 
                                            height="100%"
                                            style={{
                                                '@media print': {
                                                    printColorAdjust: 'exact',
                                                    WebkitPrintColorAdjust: 'exact'
                                                }
                                            }}
                                        >
                                            <PieChart>
                                                <Pie
                                                    data={distribution}
                                                    cx="50%"
                                                    cy="45%"
                                                    innerRadius={35}
                                                    outerRadius={65}
                                                    dataKey="count"
                                                    labelLine={false}
                                                >
                                                    {distribution.map((entry, index) => (
                                                        <Cell 
                                                            key={`cell-${index}`} 
                                                            fill={entry.color}
                                                            stroke="#fff"
                                                            strokeWidth={1}
                                                        />
                                                    ))}
                                                </Pie>

                                                <Legend
                                                    layout="vertical"
                                                    verticalAlign="top"
                                                    align="right"
                                                    iconSize={8}
                                                    wrapperStyle={{
                                                        fontSize: '8px',
                                                        paddingLeft: '10px',
                                                        right: '0px',
                                                        top: '10px'
                                                    }}
                                                    formatter={(value, entry, index) => {
                                                        const item = distribution[index];
                                                        const percentage = ((item.count / distribution.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(0);
                                                        return (
                                                            <span style={{ 
                                                                color: item.color, 
                                                                fontSize: '8px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {value}: {item.count} ({percentage}%)
                                                            </span>
                                                        );
                                                    }}
                                                />

                                                <RechartsTooltip
                                                    formatter={(value, name) => [`${value} users`, name]}
                                                    contentStyle={{
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '8px',
                                                        fontSize: '11px'
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Box>
    );
};

export default PerformanceDistribution;